import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
} from "reactstrap";
import { css } from "react-emotion";
import { Formik } from "formik";
import swal from "sweetalert2";
import { handleError } from "@/util/errorHandler";

import users from "@/api/users";
import { isPhoneNumber, clearPhoneNumber } from "@/util/validationHelper";
import InputMask from "react-input-mask";

const modalStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  overflow: visible;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  width: 35rem;

  .buttons {
    display: flex;
    justify-content: space-between;
    margin: 15px 0 0;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 1021; /* above navs, below sweetalert */
`;

class ChangePhoneModal extends React.Component {
  state = {
    phoneNumber: "",
  };

  baseState = {
    ...this.state,
  };

  validateFormik = ({ phoneNumber }) => {
    const errors = {};

    if (phoneNumber.length === 0) {
      errors.phoneNumber = "Phone number is required";
    }

    if (phoneNumber && phoneNumber.length && !isPhoneNumber(phoneNumber)) {
      errors.phoneNumber = "Incorrect phone number";
    }

    if (phoneNumber && phoneNumber[5] === "0") {
      errors.phoneNumber = "Phone number can’t start with 0";
    }

    return errors;
  };

  reset = () => {
    this.setState(this.baseState);
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  onSendChangePhoneRequest = async ({ accountId, phoneNumber }) => {
    try {
      const { id, status } = await users.createChangePhoneRequest({
        accountId,
        replacementPhoneNumber: clearPhoneNumber(phoneNumber),
      });

      if (status === "OPEN") {
        this.askVerifyChangePhoneRequest(id);
      } else {
        await swal(
          "Warning!",
          `Request already created, please check it on change phone requests page.`,
          "warning",
        );
        this.beforeClose();
      }
    } catch (err) {
      handleError(err);
    }
  };

  askVerifyChangePhoneRequest = async id => {
    const html = `
      <span>
        Do you want to verify this request?
      </span>
      <br/>
      <span>Please, enter SMS code in the input below.</span>
    `;

    try {
      await swal({
        title: `Verify request?`,
        html,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        showLoaderOnConfirm: true,
        input: "text",
        preConfirm: async code => {
          await users.verifyChangePhoneRequest({ requestId: id, code });
          await swal("Success!", `Request successfully verified.`, "success");
        },
      });
    } catch (err) {
      handleError(err);
    } finally {
      this.beforeClose();
    }
  };

  render() {
    const form = (
      <Formik
        initialValues={{
          accountId: this.props.userAccountId,
          phoneNumber: this.state.phoneNumber,
        }}
        validate={this.validateFormik}
        onChange={this.validateFormik}
        onSubmit={this.onSendChangePhoneRequest}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <FormGroup row>
                <Label sm={3}>User account ID</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="accountId"
                    placeholder="Enter user's account id"
                    invalid={Boolean(errors.accountId)}
                    value={values.accountId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                  />
                  {errors.accountId && (
                    <FormFeedback className="text-alert">
                      {errors.accountId}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Phone Number</Label>
                <Col sm={9}>
                  <InputMask
                    name="phoneNumber"
                    mask="+855 99 999 9999"
                    maskChar=""
                    value={values.phoneNumber}
                    onChange={handleChange}>
                    {inputProps => (
                      <Input
                        {...inputProps}
                        placeholder="Enter new phone number"
                        type="text"
                        autocomplete="off"
                        disableUnderline
                        invalid={Boolean(errors.phoneNumber)}
                      />
                    )}
                  </InputMask>
                  {errors.phoneNumber && (
                    <FormFeedback className="text-alert">
                      {errors.phoneNumber}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
            <CardFooter>
              <div className="buttons">
                <Button color="primary" type="submit">
                  <span>Create change phone request</span>
                </Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.beforeClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button close onClick={this.beforeClose} />

            <span>Change phone number</span>
          </CardHeader>
          {form}
        </Card>
      </Modal>
    );
  }
}

ChangePhoneModal.defaultProps = {
  isOpen: false,
  onModalClose: () => {},
  userAccountId: "",
};

ChangePhoneModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  userAccountId: PropTypes.string.isRequired,
};

export default ChangePhoneModal;
